import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Avatar from '../components/Avatar';
import GroupAvatar from './GroupAvatar';
import { AddMembersDialog } from './GroupDialogs';
import { LeaveGroupDialog } from './ConfirmDialogs';
import { ContextMenu, MenuItem, Icons } from './Menu';
import { chatName, displayName, dmApi, STATUS_LABEL } from './format';

const STATUS_ORDER = { online: 0, dnd: 1, away: 2, offline: 3 };

// Side panel of a group: members with status, owner crown and owner actions (remove, make owner),
// plus "Add people" and "Leave group"
export default function GroupMembers({ group, me, myStatus, nicknames, presence, closeButton, onChanged }) {
  const navigate = useNavigate();
  const [menu, setMenu] = useState(null); // { member, x, y }
  const [dialog, setDialog] = useState(null); // 'add' | 'leave'
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const isOwner = group.owner_id === me;

  const statusOf = (id) => (id === me ? myStatus : presence[id] || 'offline');
  const members = [...group.members].sort((a, b) =>
    (b.id === group.owner_id) - (a.id === group.owner_id)
    || STATUS_ORDER[statusOf(a.id)] - STATUS_ORDER[statusOf(b.id)]
    || displayName(a, nicknames).localeCompare(displayName(b, nicknames)));

  const act = async (fn) => {
    setBusy(true);
    setError('');
    try { await fn(); onChanged(); } catch (err) { setError(err.response?.data?.error || 'Something went wrong.'); }
    setBusy(false);
    setMenu(null);
  };
  const removeMember = (m) => act(() => dmApi.delete(`/api/dm/groups/${group.id}/members/${m.id}`));
  const makeOwner = (m) => act(() => dmApi.post(`/api/dm/groups/${group.id}/owner`, { user_id: m.id }));
  const leave = async () => {
    setBusy(true);
    try {
      await dmApi.delete(`/api/dm/groups/${group.id}/members/${me}`);
      navigate('/dm', { replace: true });
    } catch { setBusy(false); }
  };

  const name = chatName(group, { nicknames, meId: me });

  return (
    <div className="flex min-h-full flex-col">
      <div className="relative shrink-0 bg-brand px-4 pb-4 pt-[calc(20px_+_var(--safe-top))]">
        {closeButton}
        <GroupAvatar icon={group.icon} members={group.members.filter(m => m.id !== me)} size={64} />
        <div className="mt-3 break-words font-display text-lg font-extrabold text-white">{name}</div>
        <div className="text-xs" style={{ color: 'rgba(255,255,255,0.75)' }}>{group.members.length} of {group.max_members || 10} members</div>
      </div>

      <div className="flex-1 px-2 py-3">
        <div className="px-2 pb-1 text-[11px] font-extrabold uppercase tracking-wider text-text-dim">Members</div>
        {members.map(m => {
          const status = statusOf(m.id);
          const owner = m.id === group.owner_id;
          return (
            <div
              key={m.id}
              role="button"
              tabIndex={0}
              onClick={() => navigate(`/profile/${m.username}`)}
              onKeyDown={e => { if (e.key === 'Enter') navigate(`/profile/${m.username}`); }}
              onContextMenu={isOwner && m.id !== me ? (e => { e.preventDefault(); setMenu({ member: m, x: e.clientX, y: e.clientY }); }) : undefined}
              className="group flex cursor-pointer items-center gap-3 rounded px-2 py-1.5 hover:bg-surface-2"
            >
              <Avatar src={m.avatar} size={32} status={status === 'offline' ? undefined : status} />
              <span className={`min-w-0 flex-1 ${status === 'offline' ? 'opacity-60' : ''}`}>
                <span className="flex items-center gap-1.5">
                  <span className="truncate font-display text-sm font-bold text-text">{displayName(m, nicknames)}{m.id === me && ' (you)'}</span>
                  {owner && <span className="flex shrink-0 text-amber" title="Group owner" aria-label="Group owner" style={{ transform: 'scale(0.8)' }}>{Icons.crown}</span>}
                </span>
                <span className="block truncate text-xs text-text-dim">{STATUS_LABEL[status] || 'Offline'}</span>
              </span>
              {isOwner && m.id !== me && (
                <button
                  type="button"
                  aria-label={`Options for ${displayName(m, nicknames)}`}
                  onClick={e => { e.stopPropagation(); setMenu({ member: m, x: e.clientX, y: e.clientY }); }}
                  className="flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center rounded-full border-0 bg-transparent text-text-dim hover:bg-surface-3 hover:text-text"
                >
                  {Icons.more}
                </button>
              )}
            </div>
          );
        })}
        {error && <div className="px-2 pt-2 text-xs text-red">{error}</div>}
      </div>

      <div className="flex shrink-0 flex-col gap-2 px-4 pb-4">
        <button
          type="button"
          onClick={() => setDialog('add')}
          disabled={group.members.length >= (group.max_members || 10)}
          className="flex w-full cursor-pointer items-center justify-center gap-2 rounded-sm border-0 bg-surface-3 py-2 font-display text-sm font-bold text-text hover:bg-border disabled:cursor-not-allowed disabled:opacity-50"
        >
          {Icons.userPlus} Add people
        </button>
        <button
          type="button"
          onClick={() => setDialog('leave')}
          className="flex w-full cursor-pointer items-center justify-center gap-2 rounded-sm border-0 bg-surface-3 py-2 font-display text-sm font-bold text-red hover:bg-border"
        >
          {Icons.logout} Leave group
        </button>
      </div>

      {menu && (
        <ContextMenu x={menu.x - 180} y={menu.y} onClose={() => setMenu(null)}>
          <MenuItem icon={Icons.crown} disabled={busy} onClick={() => makeOwner(menu.member)}>Make group owner</MenuItem>
          <MenuItem icon={Icons.logout} danger disabled={busy} onClick={() => removeMember(menu.member)}>Remove from group</MenuItem>
        </ContextMenu>
      )}
      {dialog === 'add' && <AddMembersDialog conversation={group} nicknames={nicknames} onClose={() => { setDialog(null); onChanged(); }} />}
      {dialog === 'leave' && <LeaveGroupDialog name={name} isOwner={isOwner} busy={busy} onConfirm={leave} onClose={() => setDialog(null)} />}
    </div>
  );
}
